import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash';
import { RefreshCw, FileSpreadsheet, TrendingDown, DollarSign, BarChart3, AlertCircle, X } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const OveragesAnalysis = ({ user, userRole, onClose, currentProject, projects, onProjectChange }) => {
  const [showOveragesModal, setShowOveragesModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showAvgModal, setShowAvgModal] = useState(false);
  const [overagesData, setOveragesData] = useState([]);
  const [balanceData, setBalanceData] = useState([]);
  const [avgOverageData, setAvgOverageData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Auto-load data when component mounts or project changes
  useEffect(() => {
    if (currentProject) {
      processFirestoreData();
    }
  }, [currentProject?.id]);

  const processFirestoreData = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const swornStatementsRef = collection(db, 'swornStatements');
      const snapshot = await getDocs(swornStatementsRef);

      if (snapshot.empty) {
        setError('No sworn statements found in Firestore.');
        setIsProcessing(false);
        return;
      }

      const overagesResult = [];
      const balanceResult = [];
      const itemStats = {};

      snapshot.forEach((doc) => {
        const data = doc.data();
        const projectId = doc.id;

        // Skip special documents like historicalJobs
        if (projectId === 'historicalJobs') {
          return;
        }

        // Always process all projects - removed currentProject filter
        // Get project name from projects array
        const project = projects?.find(p => p.id === projectId);
        const projectName = project?.name || projectId;

        // Calculate contract totals
        const contractPrice = Number(data.contractPrice) || 0;
        const downPayment = Number(data.downPayment) || 0;

        // Calculate total draws received
        let totalDraws = 0;
        if (data.drawsReceived && Array.isArray(data.drawsReceived)) {
          totalDraws = data.drawsReceived.reduce((sum, draw) => {
            const drawAmount = Number(draw) || 0;
            return sum + drawAmount;
          }, 0);
        }

        // Calculate balance: what's left to pay
        const totalReceived = downPayment + totalDraws;
        const balance = contractPrice - totalReceived;

        // Add to balance data if there's a balance
        if (contractPrice > 0 && balance !== 0) {
          balanceResult.push({
            projectId,
            projectName,
            contractPrice,
            totalReceived,
            balance
          });
        }

        // Check for overages (negative balance means they've paid more than contract)
        if (balance < 0) {
          overagesResult.push({
            projectId,
            projectName,
            item: 'Contract Total',
            category: 'Project Level',
            originalBid: contractPrice,
            totalDraws: totalReceived,
            overage: Math.abs(balance),
            percentOver: contractPrice > 0 ? (Math.abs(balance) / contractPrice * 100) : 0
          });
        }

        // Analyze individual line items for overages
        if (data.items && typeof data.items === 'object') {
          Object.entries(data.items).forEach(([itemKey, itemData]) => {
            // Skip if itemData is not an object
            if (!itemData || typeof itemData !== 'object') return;

            // Parse the item key (format: "Category|||ItemName")
            const [category, itemName] = itemKey.split('|||');
            
            if (!category || !itemName) return;

            // Safely extract values with fallbacks
            const originalBid = Number(itemData.originalBidCost) || 0;
            const changeOrders = Number(itemData.changeOrders) || 0;
            const draws = Array.isArray(itemData.draws) ? itemData.draws : [];
            const contractorName = typeof itemData.contractorName === 'string' ? itemData.contractorName : '';
            
            // Calculate total draws for this item
            const itemTotalDraws = draws.reduce((sum, draw) => sum + (Number(draw) || 0), 0);
            
            // Calculate item balance
            const itemBalance = (originalBid + changeOrders) - itemTotalDraws;
            
            // If item has overages (negative balance)
            if (itemBalance < 0 && originalBid > 0) {
              const overage = Math.abs(itemBalance);
              const percentOver = (overage / originalBid) * 100;
              
              overagesResult.push({
                projectId,
                projectName,
                item: itemName,
                category,
                originalBid,
                changeOrders,
                totalDraws: itemTotalDraws,
                overage,
                percentOver
              });

              // Track statistics for this item type
              const statKey = `${category} - ${itemName}`;
              if (!itemStats[statKey]) {
                itemStats[statKey] = {
                  count: 0,
                  totalPercent: 0,
                  totalOverage: 0,
                  overCount: 0,
                  underCount: 0,
                  projects: [],
                  contractors: new Set()
                };
              }

              itemStats[statKey].count++;
              itemStats[statKey].totalPercent += percentOver;
              itemStats[statKey].totalOverage += overage;
              itemStats[statKey].overCount++;
              itemStats[statKey].projects.push(projectName);
              if (contractorName) {
                itemStats[statKey].contractors.add(contractorName);
              }
            }
            // Track items that are under budget for statistics
            else if (originalBid > 0) {
              const statKey = `${category} - ${itemName}`;
              if (!itemStats[statKey]) {
                itemStats[statKey] = {
                  count: 0,
                  totalPercent: 0,
                  totalOverage: 0,
                  overCount: 0,
                  underCount: 0,
                  projects: [],
                  contractors: new Set()
                };
              }

              itemStats[statKey].count++;
              itemStats[statKey].underCount++;
              itemStats[statKey].projects.push(projectName);
              
              if (contractorName) {
                itemStats[statKey].contractors.add(contractorName);
              }
              
              // Calculate how much under budget (positive balance)
              if (itemBalance > 0) {
                const percentUnder = (itemBalance / originalBid) * 100;
                itemStats[statKey].totalPercent -= percentUnder; // Negative for under budget
              }
            }
          });
        }

        // Track project-level statistics
        if (contractPrice !== 0) {
          const itemKey = 'Project Totals';
          const percentOver = (balance / contractPrice) * -1;

          if (!itemStats[itemKey]) {
            itemStats[itemKey] = {
              count: 0,
              totalPercent: 0,
              totalOverage: 0,
              overCount: 0,
              underCount: 0,
              projects: []
            };
          }

          itemStats[itemKey].count++;
          itemStats[itemKey].totalPercent += percentOver;
          
          if (balance < 0) {
            itemStats[itemKey].overCount++;
            itemStats[itemKey].totalOverage += Math.abs(balance);
          } else if (balance > 0) {
            itemStats[itemKey].underCount++;
          }

          itemStats[itemKey].projects.push(projectName);
        }
      });

      // Build average overages data
      const avgResult = [];
      Object.entries(itemStats).forEach(([item, stat]) => {
        const avgPercent = stat.count > 0 ? stat.totalPercent / stat.count : 0;
        const avgOverage = stat.overCount > 0 ? stat.totalOverage / stat.overCount : 0;
        console.log('Item Stats:', item, stat);
        
        // Determine contractor display
        let contractorDisplay = '';
        if (stat.contractors && stat.contractors.size > 0) {
          const contractorArray = Array.from(stat.contractors);
          if (contractorArray.length === 1) {
            contractorDisplay = contractorArray[0];
          } else {
            contractorDisplay = 'Different contractors';
          }
        }

        avgResult.push({
          item,
          jobsCount: stat.count,
          avgPercent,
          avgOverage,
          overCount: stat.overCount,
          underCount: stat.underCount,
          info: `${stat.overCount} over, ${stat.underCount} under`,
          projects: stat.projects,
          contractor: contractorDisplay
        });
      });

      // Sort by highest average overage percentage
      avgResult.sort((a, b) => Math.abs(b.avgPercent) - Math.abs(a.avgPercent));

      setOveragesData(overagesResult);
      setBalanceData(balanceResult);
      setAvgOverageData(avgResult);

    } catch (err) {
      console.error('Error fetching Firestore data:', err);
      setError(err.message || 'Failed to fetch data from Firestore');
    } finally {
      setIsProcessing(false);
    }
  };

  const DetailModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '1rem'
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          position: 'relative'
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            borderBottom: '1px solid #E5E7EB',
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 10
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{title}</h2>
            <button onClick={onClose} 
              style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', 
                borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={24} color="#6B7280" />
            </button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1400px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          borderBottom: '1px solid #E5E7EB',
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileSpreadsheet style={{ width: '28px', height: '28px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937', margin: 0 }}>Overages Analysis</h1>
          </div>
          <button onClick={onClose} 
            style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', 
              borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={24} color="#6B7280" />
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            {error && (
              <div style={{ 
                background: '#fee', 
                border: '1px solid #fcc',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <AlertCircle style={{ width: '20px', height: '20px', color: '#c33', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#c33' }}>Error</p>
                  <p style={{ margin: '4px 0 0 0', color: '#c33', fontSize: '14px' }}>{error}</p>
                </div>
              </div>
            )}

            <button
              onClick={processFirestoreData}
              disabled={isProcessing}
              style={{
                padding: '10px 20px',
                backgroundColor: isProcessing ? '#9CA3AF' : '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isProcessing ? (
                <>
                  <RefreshCw style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }} />
                  Loading from Firebase...
                </>
              ) : (
                <>
                  <RefreshCw style={{ width: '20px', height: '20px' }} />
                  Refresh Data
                </>
              )}
            </button>
          </div>

          {/* Stats Cards */}
          {(overagesData.length > 0 || balanceData.length > 0 || avgOverageData.length > 0) && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {/* Overages Card */}
              {overagesData.length > 0 && (
                <div style={{ 
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  padding: '1.5rem',
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                    <div style={{ background: '#fee', padding: '12px', borderRadius: '8px' }}>
                      <TrendingDown style={{ width: '24px', height: '24px', color: '#c33' }} />
                    </div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Overages Report</h2>
                  </div>
                  <div style={{ background: '#fee', borderRadius: '6px', padding: '12px', marginBottom: '1rem' }}>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#c33' }}>
                      {overagesData.length}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>Items with overages</p>
                  </div>
                  <button
                    onClick={() => setShowOveragesModal(true)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#c33',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View Details
                  </button>
                </div>
              )}

              {/* Balance Card */}
              {/* {balanceData.length > 0 && (
                <div style={{ 
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  padding: '1.5rem',
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                    <div style={{ background: '#e3f2fd', padding: '12px', borderRadius: '8px' }}>
                      <DollarSign style={{ width: '24px', height: '24px', color: '#1976d2' }} />
                    </div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Balance Values</h2>
                  </div>
                  <p style={{ color: '#666', marginBottom: '1rem', fontSize: '13px' }}>
                    Current balance for each project
                  </p>
                  <div style={{ background: '#e3f2fd', borderRadius: '6px', padding: '12px', marginBottom: '1rem' }}>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#1976d2' }}>
                      {balanceData.length}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>Projects tracked</p>
                  </div>
                  <button
                    onClick={() => setShowBalanceModal(true)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View Details
                  </button>
                </div>
              )} */}

              {/* Average Card */}
              {avgOverageData.length > 0 && (
                <div style={{ 
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  padding: '1.5rem',
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                    <div style={{ background: '#f3e5f5', padding: '12px', borderRadius: '8px' }}>
                      <BarChart3 style={{ width: '24px', height: '24px', color: '#7b1fa2' }} />
                    </div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Statistics</h2>
                  </div>
                  <div style={{ background: '#f3e5f5', borderRadius: '6px', padding: '12px', marginBottom: '1rem' }}>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#7b1fa2' }}>
                      {avgOverageData.length}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>Items analyzed</p>
                  </div>
                  <button
                    onClick={() => setShowAvgModal(true)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#7b1fa2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View Details
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modals */}
      <DetailModal
        isOpen={showOveragesModal}
        onClose={() => setShowOveragesModal(false)}
        title="Overages Report"
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Project</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Item</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Overage</th>
            </tr>
          </thead>
          <tbody>
            {overagesData.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '12px', fontSize: '14px' }}>{row.projectName}</td>
                <td style={{ padding: '12px', fontSize: '14px' }}>{row.item}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <span style={{ color: '#c33', fontWeight: 'bold' }}>
                    {formatCurrency(row.overage)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailModal>

      <DetailModal
        isOpen={showBalanceModal}
        onClose={() => setShowBalanceModal(false)}
        title="Project Balance Details"
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Project</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Contract</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Received</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {balanceData.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '12px', fontSize: '14px' }}>{row.projectName}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>
                  {formatCurrency(row.contractPrice)}
                </td>
                <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>
                  {formatCurrency(row.totalReceived)}
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <span style={{ 
                    fontWeight: 'bold',
                    color: row.balance < 0 ? '#c33' : row.balance > 0 ? '#2e7d32' : '#666'
                  }}>
                    {formatCurrency(row.balance)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailModal>

      <DetailModal
        isOpen={showAvgModal}
        onClose={() => setShowAvgModal(false)}
        title="Payment Statistics"
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Category</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Jobs</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Contractor</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Avg Balance %</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Details</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Projects</th>
            </tr>
          </thead>
          <tbody>
            {avgOverageData.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '12px', fontWeight: '500' }}>{row.item}</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>{row.jobsCount}</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>{row.contractor}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <span style={{ 
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    background: row.avgPercent < 0 ? '#fee' : '#e8f5e9',
                    color: row.avgPercent < 0 ? '#c33' : '#2e7d32'
                  }}>
                    {formatPercent(row.avgPercent)}
                  </span>
                </td>
                <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>{row.info}</td>
                <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                  {row.projects && row.projects.length > 0 ? (
                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {row.projects.join(', ')}
                    </div>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailModal>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default OveragesAnalysis;